import _ from "lodash";
import React, { useCallback, useState } from "react";

// Components
import UIBaseComponent from "UIComponents/UIBaseComponent";
import ImageDropzoneV2 from "UIComponents/ImageDropzoneV2";

// Design System
import {
  Dropdown,
  DropdownMenu,
  IconButton,
  ProgressIndicator,
} from "@toddle-edu/ds-web";
import {
  UploadOutlined,
  SearchOutlined,
  ImageSquareOutlined,
  DotsVerticalOutlined,
  DeleteOutlined,
} from "@toddle-edu/ds-icons";

// 3rd party
import classNames from "classnames";

// scss
import classes from "./AssessmentCoverUpload.scss";
import { onEnterOrSpaceKeyDown } from "Utils";
import { ASSESSMENT_TYPE_WORKSHEET } from "Constants/stringConstants";

/**
 *  @note this component is a custom component (alternative to ImageDropzoneV2), to be used util this type of variant is available in DS
 */

const UPLOAD_OPTIONS = [
  { key: "upload", label: "common:upload", icon: <UploadOutlined /> },
  {
    key: "search",
    label: "common:search_on_pixabay",
    icon: <SearchOutlined />,
  },
];

const DROPDOWN_ACTIONS = [
  {
    key: "remove",
    label: "common:remove",
    icon: <DeleteOutlined />,
    isDestructive: true,
  },
  { key: "divider", isDivider: true },
  {
    label: "Change cover",
    key: "common:change_with_label",
    localeParams: { label: "common:cover" },
    isItemGroup: true,
    options: UPLOAD_OPTIONS,
  },
];

const UploadComponent = props => {
  const {
    t,
    image,
    options,
    onUploadFromDevice,
    onAddFromPixabay,
    onDeleteClick,
    progressValue,
    label,
    testId,
    showSemanticColors,
  } = props;

  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  // Following state is used to control the visibility of the add cover image dropdown
  const [
    isCoverImageDropdownVisible,
    setIsAddCoverImageDropdownVisible,
  ] = useState(false);
  const [hovered, setHovered] = useState(false);

  // All below functions are used for hover states and click handling for optimization purpose
  const handleMouseEnter = useCallback(() => setHovered(true), []);
  const handleMouseLeave = useCallback(() => setHovered(false), []);
  const handleFocus = useCallback(() => setHovered(true), []);
  const handleBlur = useCallback(() => setHovered(false), []);
  const handleKeyDown = useCallback(
    e =>
      onEnterOrSpaceKeyDown(e, () =>
        setIsAddCoverImageDropdownVisible(prev => !prev)
      ),
    []
  );

  const onDropdownItemClick = e => {
    switch (e?.key) {
      case "upload": {
        onUploadFromDevice();
        break;
      }
      case "search": {
        onAddFromPixabay();
        break;
      }
      case "remove": {
        onDeleteClick();
        break;
      }
    }

    setIsAddCoverImageDropdownVisible(false);
    setIsDropdownVisible(false);
  };

  if (image) {
    const dropdownActionsClass = classNames({
      [classes.dropdownActions]: true,
      [classes.dropdownActionsVisible]: isDropdownVisible,
    });

    const options = DROPDOWN_ACTIONS.reduce((result, action) => {
      const { label, localeParams, options } = action;

      if (label) {
        const localizedLabel = localeParams ? t(label, localeParams) : t(label);

        if (options) {
          const localizedOptions = options.map(option => ({
            ...option,
            label: t(option.label),
          }));

          result.push({
            ...action,
            label: localizedLabel,
            options: localizedOptions,
          });
        } else {
          result.push({ ...action, label: localizedLabel });
        }
      } else {
        result.push(action);
      }

      return result;
    }, []);

    return (
      <div className={classes.imageContainer}>
        <img
          className={classes.coverImage}
          src={image}
          alt={"assessment cover"}
        />

        {progressValue === 100 ? (
          <Dropdown
            overlay={
              <DropdownMenu
                options={options}
                onClick={onDropdownItemClick}
                testId={`${testId}-imageDropzone-actionMenu`}
                shouldStopPropagation={true}
              />
            }
            visible={isDropdownVisible}
            onVisibleChange={setIsDropdownVisible}
          >
            <IconButton
              icon={<DotsVerticalOutlined variant="subtle" />}
              testId={`${testId}-imageDropzone-actionMenu`}
              _legacy_variant={"plain-subtle"}
              variant="neutral"
              type="outlined"
              size={"x-small"}
              className={dropdownActionsClass}
            />
          </Dropdown>
        ) : null}

        {progressValue < 100 ? (
          <div className={classes.progressIndicator}>
            <ProgressIndicator
              progress={progressValue}
              variant={"preview-loader"}
            />
          </div>
        ) : null}

        {progressValue < 100 ? <div className={classes.overlay} /> : null}
      </div>
    );
  }

  const uploadButton = () => {
    const buttonText = t("common:add_a_with_label", {
      label: label || t("classRoom:cover_image"),
    });

    const buttonClasses = classNames(classes.uploadButton, {
      [classes.dropdownButtonActive]: isCoverImageDropdownVisible,
    });

    return (
      <div
        className={buttonClasses}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        tabIndex="0"
        role="button"
        aria-label={buttonText}
        aria-expanded={isCoverImageDropdownVisible}
        data-testid={`${testId}-upload-button`}
      >
        <ImageSquareOutlined
          variant={
            hovered || isCoverImageDropdownVisible ? "default" : "subtle"
          }
          size="xx-small"
          aria-hidden="true"
          className={classNames({
            [classes.semanticInfoColor]: showSemanticColors,
          })}
        />
        <span
          className={classNames("text-label-l", {
            [classes.hoverText]: hovered || isCoverImageDropdownVisible,
            [classes.semanticInfoColor]: showSemanticColors,
          })}
        >
          {buttonText}
        </span>
      </div>
    );
  };

  return (
    <div
      onClick={e => {
        e?.stopPropagation();
      }}
      className={classes.uploadButtonContainer}
    >
      <Dropdown
        overlay={
          <DropdownMenu
            options={options}
            onClick={onDropdownItemClick}
            shouldStopPropagation={true}
            testId={`${testId}-upload-options-menu`}
          />
        }
        visible={isCoverImageDropdownVisible}
        onVisibleChange={setIsAddCoverImageDropdownVisible}
        trigger={["click"]}
      >
        {uploadButton()}
      </Dropdown>
    </div>
  );
};

class AssessmentCoverUpload extends UIBaseComponent {
  constructor(props) {
    super(props);
  }

  renderUploadComponent = () => {
    const { t, label, testId, assessmentType } = this.props;
    const isWorksheet = assessmentType === ASSESSMENT_TYPE_WORKSHEET;

    const options = _.map(UPLOAD_OPTIONS, option => {
      return {
        ...option,
        label: t(option.label),
      };
    });

    return (
      <ImageDropzoneV2
        {..._.omit(this.props, ["label", "labelComponent"])}
        customUploadComponent={
          <UploadComponent
            t={t}
            testId={testId}
            options={options}
            label={label}
            showSemanticColors={isWorksheet}
          />
        }
        dsVersion="2.0"
      />
    );
  };

  renderUploadViewComponent = () => {
    const { value: image } = this.props;

    if (image) {
      return (
        <div className={classes.imageContainer}>
          <img
            className={classes.coverImage}
            src={image}
            alt={"assessment cover view"}
          />
        </div>
      );
    }

    return null;
  };

  renderLabel = () => null;

  renderView = () => this.renderUploadViewComponent();
  renderEdit = () => this.renderUploadComponent();
}

AssessmentCoverUpload.defaultProps = {
  ...UIBaseComponent.defaultProps,
  testId: "IBcomponents-assessmentCoverUpload",
};
export default AssessmentCoverUpload;
